/**
 * 🔴 **Web 键盘流的唯一实现** —— `AC-O2.8`（O 域 PRD）/ P0 清单 §1 硬指标。
 *
 * ## 依据（三处交叉验证）
 *
 * | 来源 | 原文 |
 * |---|---|
 * | P0 功能全景清单 §1 | 「Web 支持键盘流（**回车保存、数字键导航**）」 |
 * | O 域 PRD `AC-O2.8` | 「Web 支持键盘流（回车保存 · **数字键导航**）」 |
 * | 设计语言规范 §4.6 键盘约定 | 「所有控件 `Tab` 可达；`Enter` 在主表单中即保存；**数字键 `1–9`** 在导入向导中直选分类」 |
 *
 * 🔴 **注意一条易混**：「数字键 `1–9`」在设计里有**两个不相干的用途** ——
 * ① **全局导航**（切侧栏已实现的项）= 本文件；② **导入向导内直选分类** = `04-账单导入` 原型 `kbd-bar`，属 D 域，**未落地**。
 * 本批只做 ①，**不代做 ②**（D 域批次自己实现，且那时应把快捷键收敛到同一个内核 —— 见下方「将来怎么扩」）。
 *
 * ## 🔴 为什么抽成纯逻辑层（而不是写在 `Shell.tsx` 的 effect 里）
 *
 * 端侧测试基座是 `renderToStaticMarkup`（**零新依赖** → 无 DOM、**不执行 effect**，见 `test/render.tsx`）。
 * 若把键位映射直接写进 `useEffect`，本规则在单测里**一行都测不到**，只能靠"看起来对"。
 * ⇒ 与 `AnchorBar.tsx` 的 `pickAnchors()`、`txn-draft.ts` 同一范式：**规则与副作用分离**。
 *
 * ```
 *   键盘事件（真实 DOM） ──▶ isTypingTarget() 守卫 ──▶ resolveShortcut() ──▶ Shortcut
 *        ↑ effect 层（薄）                                   ↑ 纯逻辑层（零 DOM · 可直测）
 * ```
 *
 * ## 🔴 映射来源 = `implementedGroups()`，**禁硬编第二份**
 *
 * 数字键只能落在**已经实现**的项上（`nav.tsx` 的判据 = `path !== undefined`）。
 * 若这里自己写一份 `['/','/accounts','/policies']`，就出现**同一事实两个载体** ——
 * 下一个实现「流水」时改了 `nav.tsx` 却忘了改这里 ⇒ 数字键静默少一个（不报错）。
 * ⇒ 本文件**不持有任何路由字面量**，只消费 `implementedGroups()` 的输出。
 *
 * ## 🔴 键位分配：按**已实现项的出现顺序**从 `1` 起编
 *
 * P0 现在只有 3 项已实现（今天 / 资产 / 保单）⇒ 键位 `1` / `2` / `3`。
 * ⚠️ **这是一个显式取舍**：将来「流水」落地（`nav.tsx` 里它排在「今天」之后）会**把资产挤到 `3`**。
 * 另一条路是"键位跟随 `NAV` 全量下标"（那样今天=`1`、资产=`6`、保单=`7`，中间留空洞）。
 * 选**连续编号**的理由：① 设计原文是「`1–9` **导航**」，用户预期是"第一项、第二项"而不是"跳过空格"；
 * ② 空洞键位会让用户按 `4` 什么也不发生 ⇒ 正是本项目禁的「点不动的控件」的键盘版。
 * ⚠️ 代价已登记：**加页面会挪键位** —— 将来若要稳定键位，须立 DR（那属于"约定"级决定）。
 *
 * ## 守卫（三条，缺一就是在输入框里打字时乱跳页）
 *
 * 1. **无修饰键** —— `Ctrl/Meta/Alt` 组合一律放过（`Ctrl K` 搜索、`Ctrl C` 复制…）；
 * 2. **焦点不在可编辑元素上** —— `INPUT` / `TEXTAREA` / `SELECT` / `contentEditable`；
 * 3. **`defaultPrevented` 放过** —— 上游已处理的事件不重复处理。
 *
 * 🔴 `Shift` **不算修饰键**（`Shift` + 数字 = `!@#` 等符号 ⇒ 天然不会命中 `1`–`9`，无需额外判断）。
 */

import { implementedGroups, type NavItem } from './nav.js';

/* ───────────────────────── 类型 ───────────────────────── */

/** 一次快捷键解析的结果 */
export type Shortcut =
  /** 跳到某个路由（数字键导航 / `N` 记一笔） */
  | { readonly kind: 'navigate'; readonly to: string }
  /** 认识这个键，但当前无事可做（预留：将来 `E` 编辑 / `Space` 勾选…） */
  | { readonly kind: 'none' };

/** 可自定义的键位表（spec 用；生产用默认值） */
export interface KeyBinding {
  /** 导航键：数字 `1`–`9` → 路由 */
  readonly digitNav: readonly { readonly digit: string; readonly to: string; readonly label: string }[];
}

/* ───────────────────────── 纯逻辑层（零 DOM · 可直测） ───────────────────────── */

/**
 * 🔴 **数字键 → 导航项 的映射**（唯一实现 · 从 `implementedGroups()` 现算）。
 *
 * ⚠️ 返回的是**扁平顺序**：按 `implementedGroups()` 的「组内顺序、组间顺序」拉平。
 * 组的标题（日常 / 家底 / 协作）**不占键位** —— 它不是可导航目的地。
 *
 * 🔴 数字上限 `9`（设计原文「`1–9`」）；若某天已实现项 > 9，**多出来的不给键位**
 * （不是截断成 `1–9` 循环 —— 循环会让同一个键指向两个页面）。
 */
export function digitNavBindings(): readonly { digit: string; to: string; label: string }[] {
  const flat: NavItem[] = implementedGroups().flatMap((g) => [...g.items]);
  return flat
    .filter((it): it is NavItem & { path: string } => typeof it.path === 'string')
    .slice(0, 9)
    .map((it, i) => ({ digit: String(i + 1), to: it.path, label: it.label }));
}

/** 默认键位表（生产路径；spec 也可直接读它做"对字"断言） */
export function defaultKeyBinding(): KeyBinding {
  return { digitNav: digitNavBindings() };
}

/**
 * 焦点是否在**可编辑元素**上 —— 在输入框里打字时**绝不能**触发全局快捷键。
 *
 * 🔴 判据用 `tagName` + `isContentEditable`（与 `Shell.tsx` 既有实现同源），
 * 而**不是** `document.activeElement` —— 事件目标才是"这次按键落在哪"。
 * ⚠️ 参数类型放宽到 `unknown`：纯逻辑层不 import DOM 类型，spec 传可控的替身即可。
 */
export function isTypingTarget(el: unknown): boolean {
  if (el === null || typeof el !== 'object') return false;
  const node = el as { tagName?: unknown; isContentEditable?: unknown };
  const tag = typeof node.tagName === 'string' ? node.tagName.toUpperCase() : '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return node.isContentEditable === true;
}

/** 一次按键的**最小描述**（纯逻辑层的输入形状 —— 不装 DOM `KeyboardEvent`） */
export interface KeyPress {
  /** `KeyboardEvent.key` 的值（如 `'1'` / `'n'` / `'N'`） */
  readonly key: string;
  /** 是否按了 `Ctrl` / `Meta`（⌘） */
  readonly mod?: boolean;
  /** 是否按了 `Alt` */
  readonly alt?: boolean;
  /** 事件目标（用于「在输入框里」守卫） */
  readonly target?: unknown;
  /** `KeyboardEvent.defaultPrevented`（上游已处理 ⇒ 放过） */
  readonly defaultPrevented?: boolean;
}

/**
 * 🔴 **一次按键 → 一个动作**（本模块的入口 · 纯函数）。
 *
 * 判定顺序（**顺序本身是规则**，改动要连 spec 一起改）：
 * ```
 * ① defaultPrevented / 修饰键  ⇒ none（放过，交给别的处理器）
 * ② 焦点在可编辑元素           ⇒ none（在输入框里打字）
 * ③ 'n' / 'N'                 ⇒ navigate('/new')   ← AC-O2.8「记一笔」
 * ④ '1'–'9'                   ⇒ 对应导航项         ← AC-O2.8「数字键导航」
 * ⑤ 其它                       ⇒ none
 * ```
 *
 * 🔴 `'/'`（`NumpadDivide`）等**不处理**：小键盘数字的 `key` 本身就是 `'1'`–`'9'`，
 * 无需按 `code` 区分（`key` 是布局无关的**字符**，`code` 才是物理键位）。
 */
export function resolveShortcut(press: KeyPress, binding: KeyBinding = defaultKeyBinding()): Shortcut {
  if (press.defaultPrevented === true) return { kind: 'none' };
  // ⚠️ `Shift` 刻意**不**列入：`Shift` + 数字产生符号字符，天然不命中 `'1'`–`'9'`
  if (press.mod === true || press.alt === true) return { kind: 'none' };
  if (isTypingTarget(press.target)) return { kind: 'none' };

  const key = press.key;
  if (key === 'n' || key === 'N') return { kind: 'navigate', to: '/new' };

  const hit = binding.digitNav.find((b) => b.digit === key);
  if (hit !== undefined) return { kind: 'navigate', to: hit.to };

  return { kind: 'none' };
}

/* ───────────────────────── 说明：将来怎么扩 ─────────────────────────
 *
 * D 域（账单导入）落地时会有「导入向导内 `1–9` 直选分类」——
 * 🔴 **它与本模块的全局导航是同一个键位、不同的作用域**。
 * 正解**不是**在导入页里再写一个 `addEventListener`（两份键盘实现 ⇒ 顺序不可控、
 * 且无法表达"向导打开时全局导航应让位"），而是：
 *   ① 本模块扩 `scope?: 'global' | 'import'` 参数；
 *   ② `resolveShortcut` 先判作用域，向导态下 `1–9` 走分类、否则走导航；
 *   ③ 调用侧（导入页）把 scope 传进来 —— **仍然只有一个监听器**。
 * 现在不提前实现：P0 没有第二个作用域的输入源（`DR-002` 需求先行）。
 */
